import { Alert, Badge, Button, EmptyState, FieldBlock, Input, LoadingState, useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { FormRow, FormScreen, FormSection } from '../../components/FormScreen.tsx'
import { api } from '../../lib/api.ts'
import { ColorField, SwitchCard, normalizarCodigo } from './catalog-shared.tsx'

/**
 * Cadastro de Prioridades (SPEC-20260815-0821), padrao FormScreen. A ordem
 * (sortOrder) define a fila dos quadros e seletores — menor primeiro.
 */

type PriorityList = Awaited<ReturnType<Awaited<ReturnType<(typeof api.catalog)['priorities']['$get']>>['json']>>
type PriorityRow = PriorityList['priorities'][number]

type FormPrioridade = {
  namePt: string
  nameEn: string
  code: string
  color: string | null
  isActive: boolean
  sortOrder: number
}

const VAZIO: FormPrioridade = { namePt: '', nameEn: '', code: '', color: null, isActive: true, sortOrder: 0 }

export function PrioritiesSection({ criarSinal = 0 }: { criarSinal?: number }) {
  const toast = useToast()
  const qc = useQueryClient()

  const lista = useQuery({
    queryKey: ['priorities'],
    queryFn: async () => (await api.catalog.priorities.$get()).json(),
  })

  const [busca, setBusca] = useState('')
  const [editando, setEditando] = useState<{ id: string | null } | null>(null)
  const [form, setForm] = useState<FormPrioridade>(VAZIO)
  const [confirmandoExclusao, setConfirmandoExclusao] = useState<string | null>(null)

  useEffect(() => {
    if (criarSinal > 0) abrir(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criarSinal])

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['priorities'] })
    void qc.invalidateQueries({ queryKey: ['bootstrap'] })
    void qc.invalidateQueries({ queryKey: ['i18n'] })
  }

  const aoFalhar = (erro: unknown) =>
    toast.push({
      tone: 'danger',
      message: erro instanceof Error && erro.message ? erro.message : 'Não foi possível concluir. Tente novamente.',
    })

  const corpoDe = (f: FormPrioridade) => ({
    code: normalizarCodigo(f.code || f.namePt),
    name: { 'pt-BR': f.namePt.trim(), ...(f.nameEn.trim() ? { 'en-US': f.nameEn.trim() } : {}) },
    color: f.color,
    isActive: f.isActive,
    sortOrder: f.sortOrder,
  })

  const criar = useMutation({
    mutationFn: async (f: FormPrioridade) => (await api.catalog.priorities.$post({ json: corpoDe(f) })).json(),
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Prioridade criada.' })
    },
    onError: aoFalhar,
  })

  const atualizar = useMutation({
    mutationFn: async ({ id, f }: { id: string; f: FormPrioridade }) =>
      (await api.catalog.priorities[':id'].$patch({ param: { id }, json: corpoDe(f) })).json(),
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Prioridade atualizada.' })
    },
    onError: aoFalhar,
  })

  const alternarAtivo = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      (await api.catalog.priorities[':id'].active.$post({ param: { id }, json: { isActive } })).json(),
    onSuccess: invalidar,
    onError: aoFalhar,
  })

  const excluir = useMutation({
    mutationFn: async (id: string) => (await api.catalog.priorities[':id'].$delete({ param: { id } })).json(),
    onSuccess: () => {
      invalidar()
      setConfirmandoExclusao(null)
      toast.push({ tone: 'success', message: 'Prioridade excluída.' })
    },
    onError: (erro) => {
      setConfirmandoExclusao(null)
      aoFalhar(erro)
    },
  })

  const priorities = lista.data?.priorities ?? []
  const emEdicao = editando?.id ? priorities.find((p) => p.id === editando.id) : undefined

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    if (termo === '') return priorities
    return priorities.filter((p) => `${p.name['pt-BR'] ?? ''} ${p.code}`.toLowerCase().includes(termo))
  }, [priorities, busca])

  const abrir = (alvo: PriorityRow | null) => {
    setForm(
      alvo
        ? {
            namePt: alvo.name['pt-BR'] ?? '',
            nameEn: alvo.name['en-US'] ?? '',
            code: alvo.code,
            color: alvo.color,
            isActive: alvo.isActive,
            sortOrder: alvo.sortOrder,
          }
        : { ...VAZIO, sortOrder: (priorities.at(-1)?.sortOrder ?? 0) + 10 },
    )
    setEditando({ id: alvo?.id ?? null })
  }

  const alterado =
    editando?.id == null
      ? form.namePt.trim().length >= 2
      : emEdicao !== undefined &&
        JSON.stringify(corpoDe(form)) !==
          JSON.stringify(
            corpoDe({
              namePt: emEdicao.name['pt-BR'] ?? '',
              nameEn: emEdicao.name['en-US'] ?? '',
              code: emEdicao.code,
              color: emEdicao.color,
              isActive: emEdicao.isActive,
              sortOrder: emEdicao.sortOrder,
            }),
          )

  // ------------------------------------------------------- formulário ---

  if (editando !== null) {
    const siglaDerivada = normalizarCodigo(form.code || form.namePt)
    const salvarValido = alterado && form.namePt.trim().length >= 2 && !criar.isPending && !atualizar.isPending

    return (
      <FormScreen
        title={editando.id ? 'Editar prioridade' : 'Nova prioridade'}
        subtitle={editando.id ? (emEdicao?.name['pt-BR'] ?? emEdicao?.code) : undefined}
        onClose={() => setEditando(null)}
        dirty={alterado}
        dirtyHint={editando.id ? 'Tudo salvo' : 'Preencha o nome para salvar'}
        saveLabel={editando.id ? 'Salvar' : 'Criar prioridade'}
        saveDisabled={!salvarValido}
        saving={criar.isPending || atualizar.isPending}
        // Exclusão no rodapé, à esquerda (padrão 2026-08-30).
        onDelete={
          emEdicao
            ? () => {
                const nome = emEdicao.name['pt-BR'] ?? emEdicao.code
                if (!confirm(`Excluir a prioridade “${nome}”?`)) return
                excluir.mutate(emEdicao.id, { onSuccess: () => setEditando(null) })
              }
            : undefined
        }
        deleteLabel="Excluir prioridade"
        deleteDisabled={(emEdicao?.usageCount ?? 0) > 0}
        deleteHint={(emEdicao?.usageCount ?? 0) > 0 ? 'Prioridade em uso: desative-a para tirá-la das opções' : undefined}
        onSubmit={() => {
          if (!salvarValido) return
          if (editando.id) atualizar.mutate({ id: editando.id, f: form })
          else criar.mutate(form)
        }}
      >
        <FormSection title="Informações da prioridade">
          <FormRow cols="2fr 2fr 1fr">
            <FieldBlock label="Nome da prioridade" required hint="Como aparece nas telas em português.">
              {({ id }) => (
                <Input
                  id={id}
                  autoFocus
                  value={form.namePt}
                  placeholder="Ex.: Alta"
                  onChange={(e) => setForm((f) => ({ ...f, namePt: e.target.value }))}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Name (en-US)" hint="Opcional — para quem usa a plataforma em inglês.">
              {({ id }) => (
                <Input
                  id={id}
                  value={form.nameEn}
                  placeholder="Ex.: High"
                  onChange={(e) => setForm((f) => ({ ...f, nameEn: e.target.value }))}
                />
              )}
            </FieldBlock>
            <FieldBlock
              label="Código"
              hint={
                form.code.trim() === '' && form.namePt.trim() !== ''
                    ? `Derivado do nome: ${siglaDerivada}. Digite para personalizar.`
                    : 'Identificador técnico, único no workspace.'
              }
            >
              {({ id }) => (
                <Input
                  id={id}
                  value={form.code}

                  placeholder={siglaDerivada || 'Ex.: HIGH'}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  onBlur={() => setForm((f) => ({ ...f, code: f.code ? normalizarCodigo(f.code) : f.code }))}
                />
              )}
            </FieldBlock>
          </FormRow>

          <FormRow cols="2fr 1fr">
            <ColorField value={form.color} onChange={(color) => setForm((f) => ({ ...f, color }))} />
            <FieldBlock label="Ordem" hint="Menor aparece primeiro — define a fila dos quadros.">
              {({ id }) => (
                <Input
                  id={id}
                  type="number"
                  min={0}
                  value={String(form.sortOrder)}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: Math.max(0, Number(e.target.value) || 0) }))}
                />
              )}
            </FieldBlock>
          </FormRow>

          <FormRow cols="minmax(220px, 420px)">
            <SwitchCard
              title={form.isActive ? 'Prioridade ativa' : 'Prioridade inativa'}
              description={form.isActive ? 'Aparece nas opções de Itens de Trabalho e Demandas.' : 'Some das opções; registros existentes ficam.'}
              checked={form.isActive}
              onChange={(isActive) => setForm((f) => ({ ...f, isActive }))}
            />
          </FormRow>

          {emEdicao ? (
            <p className="adm-hint">
              <i className="fas fa-link" aria-hidden />{' '}
              {emEdicao.usageCount > 0
                ? `Em uso por ${emEdicao.usageCount} ${emEdicao.usageCount === 1 ? 'Item de Trabalho' : 'Itens de Trabalho'}.`
                : 'Ainda sem uso.'}
            </p>
          ) : null}
        </FormSection>
      </FormScreen>
    )
  }

  // ------------------------------------------------------------ lista ---

  const cabecalho = (
    <div className="adm-head">
      <div>
        <h1 className="adm-head__title">Prioridades</h1>
        <p className="adm-head__desc">Níveis usados em Demandas e Itens de Trabalho — a ordem define a fila dos quadros.</p>
      </div>
      <Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>
        Incluir
      </Button>
    </div>
  )

  if (lista.isPending) return (<>{cabecalho}<LoadingState variant="list" label="Carregando as prioridades" /></>)
  if (lista.isError) {
    return (
      <>
        {cabecalho}
        <div className="adm-placeholder">
          <Alert tone="danger" title="Não foi possível carregar as prioridades"
            action={<Button variant="secondary" icon="fas fa-rotate-right" onClick={() => void lista.refetch()}>Tentar de novo</Button>}>
            Verifique a conexão e tente novamente.
          </Alert>
        </div>
      </>
    )
  }

  return (
    <>
      {cabecalho}

      <div className="adm-toolbar">
        <div className="adm-toolbar__search">
          <i className="fas fa-search" aria-hidden />
          <input type="search" placeholder="Filtrar por nome ou código…" aria-label="Filtrar prioridades"
            value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        <span className="adm-toolbar__count">
          {filtradas.length} de {priorities.length} {priorities.length === 1 ? 'registro' : 'registros'}
        </span>
      </div>

      {filtradas.length === 0 ? (
        <div className="adm-placeholder">
          <EmptyState icon="fas fa-sort-amount-up"
            title={priorities.length === 0 ? 'Nenhuma prioridade ainda' : 'Nenhuma prioridade com esse filtro'}
            description={priorities.length === 0
              ? 'Crie ao menos uma prioridade para conseguir ordenar a fila de trabalho.'
              : `Nada casa com “${busca}”.`}
            action={priorities.length === 0
              ? (<Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>Nova prioridade</Button>)
              : (<Button variant="secondary" icon="fas fa-times" onClick={() => setBusca('')}>Limpar o filtro</Button>)} />
        </div>
      ) : (
        <div className="adm-list">
          {filtradas.map((row) => {
            const confirmando = confirmandoExclusao === row.id
            const nome = row.name['pt-BR'] ?? row.code
            return (
              <div key={row.id} className={`adm-catrow${row.isActive ? '' : ' adm-catrow--off'}`}>
                <span className="adm-catrow__icon" style={row.color ? { color: row.color } : undefined}>
                  <i className="fas fa-flag" aria-hidden />
                </span>
                <span className="adm-catrow__main">
                  <span className="adm-catrow__name">{nome}</span>
                  <span className="adm-catrow__sub">
                    Código {row.code} · ordem {row.sortOrder}
                    {row.usageCount > 0 ? ` · em uso por ${row.usageCount} ${row.usageCount === 1 ? 'item' : 'itens'}` : ' · sem uso'}
                  </span>
                </span>

                {confirmando ? (
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--ob-danger)' }}>Excluir “{nome}”?</span>
                    <Button variant="danger" loading={excluir.isPending} onClick={() => excluir.mutate(row.id)}>
                      Confirmar
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirmandoExclusao(null)}>
                      Cancelar
                    </Button>
                  </span>
                ) : (
                  <>
                    <span className="adm-switchwrap">
                      <span className="adm-switchwrap__label">{row.isActive ? 'Ativa' : 'Inativa'}</span>
                      <button type="button" role="switch" aria-checked={row.isActive}
                        aria-label={`${nome} está ativa`}
                        title={row.isActive ? 'Ativa — clique para desativar' : 'Inativa — clique para ativar'}
                        className={`adm-switch${row.isActive ? ' adm-switch--on' : ''}`}
                        onClick={() => alternarAtivo.mutate({ id: row.id, isActive: !row.isActive })}>
                        <span className="adm-switch__knob" aria-hidden />
                      </button>
                    </span>
                    <button type="button" className="adm-iconbtn" aria-label={`Editar ${nome}`}
                      title="Editar" onClick={() => abrir(row)}>
                      <i className="fas fa-pen" aria-hidden />
                    </button>
                    <button type="button" className="adm-iconbtn adm-iconbtn--danger"
                      disabled={row.usageCount > 0}
                      aria-label={row.usageCount > 0 ? `${row.code} está em uso` : `Excluir ${row.code}`}
                      title={row.usageCount > 0 ? 'Prioridade em uso: desative-a para tirá-la das opções' : 'Excluir'}
                      onClick={() => setConfirmandoExclusao(row.id)}>
                      <i className="fas fa-trash-alt" aria-hidden />
                    </button>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}
